import { LoadingIcon } from '@assets/icons';
import { Button } from '@components/button';
import { DeckardFilter, SearchAndFilter } from '@components/filter';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { useLocation } from '@gatsbyjs/reach-router';
import {
  DatasetJob,
  DatasetJobFilter,
  DatasetJobSearchDetail,
  useClearDatasetJobsMutation,
} from '@generated/graphql';
import { Box, Stack, useTheme } from '@mui/material';
import { useCurrentWorkspaceId } from '@navigation/WorkspaceContext';
import { JobsManagerJobPreview } from '@subsets/workspaces';
import { useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';

interface JobsManagerProps {
  data: DatasetJob[];
  fetchMore: VoidFunction;
  loading: boolean;
  refetch: VoidFunction;
  activeFilters: boolean;
  filter: DeckardFilter<DatasetJobFilter>;
  setFilter: (filter: DeckardFilter<DatasetJobFilter>) => void;
  searchDetail: DatasetJobSearchDetail;
}

export const JobsManager = ({
  data: jobs,
  activeFilters,
  filter,
  loading,
  searchDetail,
  fetchMore,
  refetch,
  setFilter,
}: JobsManagerProps) => {
  const { palette } = useTheme();
  const { state } = useLocation();

  const workspaceId = useCurrentWorkspaceId();
  const [clearWorkspaceJobs] = useClearDatasetJobsMutation({ variables: { workspaceId } });

  const item = useCallback(
    (_index, job: DatasetJob) => (
      <JobsManagerJobPreview
        job={job}
        refetch={refetch}
        initialExpanded={(state as { jobId?: string })?.jobId === job.datasetId}
      />
    ),
    [refetch, state],
  );

  const clearJobs = async () => {
    await clearWorkspaceJobs();
    void refetch();
  };

  return (
    <Stack height="100%" gap={3}>
      <Group alignItems="center" justifyContent="space-between" pb={0}>
        <Typography variant="h3" color={palette.text.paper}>
          Running jobs
        </Typography>
        <Button variant="secondary" onClick={clearJobs}>
          Clear finished jobs
        </Button>
      </Group>
      <SearchAndFilter
        context="workspace-jobs"
        filter={filter}
        setFilter={setFilter}
        searchDetail={searchDetail}
        variant="secondary"
      />
      {loading ? (
        <Group height="100%" alignItems="center" justifyContent="center">
          <LoadingIcon size={16} />
        </Group>
      ) : jobs.length === 0 ? (
        <Group height="100%" alignItems="center" justifyContent="center">
          <Typography>
            {activeFilters ? 'No jobs found matching your search criteria.' : 'You have no jobs.'}
          </Typography>
        </Group>
      ) : (
        <Box
          data-test-id="virtuoso-item-list"
          style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}
        >
          <Virtuoso
            totalCount={jobs.length}
            overscan={1000}
            endReached={fetchMore}
            data={jobs}
            itemContent={item}
          />
        </Box>
      )}
    </Stack>
  );
};
